import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { Box, Grid } from "@mui/material";

import { ContainerWithLoader, PaginationWithLimit } from "../../components/ui";

import TableBody from "./components/TableBody";
import TableHeader from "./components/TableHeader";
import AddNewAffiliate from "./components/AddNewAffiliate";

import { APIAffiliates } from "../../api";
import usePagination from "../../hooks/usePagination";

import { ContainerBg, StyledTable } from "../../useGeneralStyles";
import AffiliatesFilter from "./components/AffilatesFilter";
import { AFFILIATES_API_URLS } from "../../api/affiliates";
import { affiliatesActions } from "../../reducers/affiliates";

export interface ISearch {
  name: null | string;
  email: null | string;
  referral_id: null | string;
}

const Affiliates = (): React.ReactElement => {
  const dispatch = useDispatch();

  const { data, count, loading } = useSelector((state) => state.affiliates.accounts);
  const { page, setPage, limit, setLimit } = usePagination();
  const [search, setSearch] = useState<ISearch>({
    name: "",
    email: "",
    referral_id: "",
  });
  const [sortBy, setSortBy] = useState({
    by: "id",
    order: false,
  });

  const { isError, error } = useQuery({
    queryKey: [AFFILIATES_API_URLS.getAffiliates, page, limit, sortBy, Object.values(search)],
    queryFn: async () => {
      dispatch(affiliatesActions.fetchAccountsRequest());
      const { data } = await APIAffiliates.getAffiliatesRequest({
        page,
        limit,
        sortBy,
        contains: search,
      });
      dispatch(affiliatesActions.fetchAccountsSuccess(data));
      return data;
    },
  });

  useEffect(() => {
    if (isError && error) {
      dispatch(affiliatesActions.fetchAccountsFailed());
      console.error("Error getting affiliate accounts request", error);
    }
  }, [isError, error]);

  return (
    <ContainerBg>
      <Grid container spacing={3}>
        <Grid item xs={9}>
          <AffiliatesFilter search={search} setSearch={setSearch} />
        </Grid>
        <Grid item xs={3} display="flex" justifyContent="end">
          <AddNewAffiliate />
        </Grid>
      </Grid>

      <Box mt="20px">
        <ContainerWithLoader isLoading={loading} report={data}>
          <StyledTable tableWithoutHover sx={{ marginTop: 25 }}>
            <TableHeader sortBy={sortBy} setSortBy={setSortBy} />
            <TableBody affiliates={data} />
          </StyledTable>
        </ContainerWithLoader>
      </Box>

      <div>
        <PaginationWithLimit
          count={count}
          limit={limit}
          setLimit={setLimit}
          page={page}
          setPageState={setPage}
        />
      </div>
    </ContainerBg>
  );
};

export default Affiliates;
